import { isNil } from 'lodash-es';
import { Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounceCallback } from 'usehooks-ts';
import { lazyReactNaiveRetry } from '../../constants/Lazy';
import useProfilePictureModal from '../../hooks/useProfilePictureModal';
import { setReviewsModalOpen } from '../../redux/ducks/UIDucks';
import {
  userGetProfile,
  userUploadProfilePictureRequest,
} from '../../redux/ducks/UserDucks';
import type { UploadProfilePictureRequestType } from '../../redux/ducks/UserDucks.types';
import type { RootReducerType } from '../../redux/reducers/RootReducer.types';
import { base64StringToBlob } from '../inputs/ImageUpload';
import Modal from './Modal';

const ImageUpload = lazyReactNaiveRetry(() => import('../inputs/ImageUpload'));

export const PROFILE_UPDATE_DELAY_MS = 500;

enum STEPS {
  UPLOAD_SCALE_PAN = 0,
  PREVIEW = 1,
  SUCCESS = 2,
}

const ProfilePictureModal = () => {
  const profilePictureModal = useProfilePictureModal();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [step, setStep] = useState(STEPS.UPLOAD_SCALE_PAN);
  const [triggerCrop, setTriggerCrop] = useState<boolean>(false);
  const [onUpload, setOnUpload] = useState<boolean>(false);
  const [loadedImage, setLoadedImage] = useState<
    HTMLImageElement | undefined
  >();
  const [validImageSetup, setValidImageSetup] = useState<boolean>(false);
  const [croppedImage, setCroppedImage] = useState<
    HTMLImageElement | undefined
  >();

  const onUploadProfilePicture = useSelector(
    (state: RootReducerType) => state.user.onUploadProfilePicture,
  );

  const uploadProfilePictureResponse = useSelector(
    (state: RootReducerType) => state.user.uploadProfilePictureResponse,
  );

  const uploadImage = () => {
    if (croppedImage) {
      const uploadBlob = base64StringToBlob(croppedImage.src);
      const imageFile = new File([uploadBlob], 'file', {
        type: uploadBlob.type,
      });

      const request: UploadProfilePictureRequestType = {
        name: 'file',
        file: imageFile,
      };

      setOnUpload(true);
      dispatch(userUploadProfilePictureRequest(request));
    }
  };

  const onBack = useCallback(() => {
    if (step === STEPS.UPLOAD_SCALE_PAN && !isNil(loadedImage)) {
      setLoadedImage(undefined);
      setTriggerCrop(false);
      setValidImageSetup(false);
      setCroppedImage(undefined);
    } else {
      setStep((value) => value - 1);
    }
  }, [step, loadedImage]);

  const onNext = () => {
    if (step === STEPS.UPLOAD_SCALE_PAN) {
      setTriggerCrop(true);
    } else if (step === STEPS.PREVIEW) {
      uploadImage();
    } else {
      setStep((value) => value + 1);
    }
  };

  const isNextStepDisabled = useMemo(() => {
    if (onUploadProfilePicture) {
      return true;
    }

    if (step === STEPS.UPLOAD_SCALE_PAN) {
      return validImageSetup !== true;
    }

    return false;
  }, [step, validImageSetup, onUploadProfilePicture]);

  const mainActionLabel = useMemo(() => {
    if (step === STEPS.PREVIEW) {
      return t('static.upload_profile_picture.modal_submit_btn_label');
    }

    return t('static.upload_profile_picture.modal_next_btn_label');
  }, [step, t]);

  const secondaryActionLabel = useMemo(() => {
    if (onUploadProfilePicture || isNil(loadedImage)) {
      return undefined;
    }

    return t('static.upload_profile_picture.modal_back_btn_label');
  }, [onUploadProfilePicture, t, loadedImage]);

  const secondaryActionDisabled: boolean = useMemo(() => {
    if (onUploadProfilePicture) {
      return true;
    }

    if (loadedImage) {
      return false;
    }

    return true;
  }, [onUploadProfilePicture, loadedImage]);

  const delayedProfileUpdate = useDebounceCallback(() => {
    dispatch(userGetProfile(true));
  }, PROFILE_UPDATE_DELAY_MS);

  useEffect(() => {
    if (!profilePictureModal.isOpen) {
      setLoadedImage(undefined);
      setTriggerCrop(false);
      setValidImageSetup(false);
      setCroppedImage(undefined);
      setStep(STEPS.UPLOAD_SCALE_PAN);
      setOnUpload(false);
    }
  }, [profilePictureModal.isOpen]);

  useEffect(() => {
    if (onUpload && uploadProfilePictureResponse) {
      if (uploadProfilePictureResponse.error) {
        toast.error(t('static.upload_profile_picture.api_upload_failure'));
      } else {
        delayedProfileUpdate();
        toast.success(t('static.upload_profile_picture.api_upload_success'));
        profilePictureModal.onClose();
      }
      setOnUpload(false);
    }
  }, [uploadProfilePictureResponse, onUpload]);

  const bodyContent = useMemo(() => {
    if (step === STEPS.PREVIEW) {
      return (
        <div className="flex min-w-full flex-col gap-4">
          {croppedImage && (
            <img
              alt="Upload"
              style={{
                objectFit: 'cover',
                minHeight: '100%',
                minWidth: '100%',
                opacity: onUploadProfilePicture ? '50%' : '100%',
              }}
              src={croppedImage.src}
            />
          )}
        </div>
      );
    }

    return (
      <Suspense fallback={<div className="hidden">&nbsp;</div>}>
        <div className="flex grow min-w-full flex-col items-center justify-center gap-4 text-center">
          <ImageUpload
            onValidSetup={setValidImageSetup}
            onCropFinished={(image: HTMLImageElement) => {
              setCroppedImage(image);
              setTriggerCrop(false);
              setStep((value) => value + 1);
            }}
            onImageLoaded={(image: HTMLImageElement) => {
              setLoadedImage(image);
            }}
            triggerCrop={triggerCrop}
            selectedImage={loadedImage}
          />
        </div>
      </Suspense>
    );
  }, [croppedImage, loadedImage, triggerCrop, step, onUploadProfilePicture]);

  return (
    <Modal
      id="ProfilePictureModal"
      setIsOpen={(open) => dispatch(setReviewsModalOpen(open))}
      disabled={isNextStepDisabled}
      loading={onUploadProfilePicture}
      isOpen={profilePictureModal.isOpen}
      title={t('static.upload_profile_picture.modal_title')}
      actionLabel={mainActionLabel}
      secondaryActionLabel={secondaryActionLabel}
      secondaryActionDisabled={secondaryActionDisabled}
      secondaryAction={onBack}
      onClose={profilePictureModal.onClose}
      onSubmit={onNext}
      body={bodyContent}
    />
  );
};

export default ProfilePictureModal;
